import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { startOfMonth, sub } from 'date-fns';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import InvoicingRow from '../../../components/InvoicingRow';
import NavButton from '../../../components/NavButton';
import { useNotificationStore } from '../../../components/notificationStore';
import { useUserStore } from '../../../components/userStore';

const CURR_YEAR = new Date().getFullYear();

const Invoicing = () => {
	const { user } = useUserStore();
	const [year, setYear] = useState(new Date().getFullYear());
	const [month, setMonth] = useState(new Date().getMonth() + 1);
	const { showSuccess, showError } = useNotificationStore();
	const lineItemsQuery = useQuery({
		queryKey: ['ilos-line-items', user.coord_agency.id, year, month],
		queryFn: async () => {
			const { data } = await axios.get(
				`/coord_agencies/${user.coord_agency.id}/ilos-line-items`,
				{
					params: {
						options: {
							year,
							month: month - 1,
						},
					},
				},
			);
			return data;
		},
	});
	const lineItemMutation = useMutation({
		mutationFn: async (lineItem) => {
			const { data } = await axios.post(
				`/requests/${lineItem.request_id}/fulfill-line-item/${lineItem.id}`,
				{ status: lineItem.status, actualCost: lineItem.actualCost },
			);
			return data;
		},
		onSuccess: () => {
			lineItemsQuery.refetch();
			showSuccess('Line item fulfillment updated');
		},
		onError: (/*error*/) => {
			showError(
				'There was a problem updating line item fulfillment. Please try again.',
			);
		},
	});

	const handleNextMonth = () => {
		const nextMonth = new Date(year, month, 1);
		setYear(nextMonth.getFullYear());
		setMonth(nextMonth.getMonth() + 1);
	};

	const handlePreviousMonth = () => {
		const previousMonth = new Date(year, month, 1);
		previousMonth.setMonth(previousMonth.getMonth() - 1);
		setYear(previousMonth.getFullYear());
		setMonth(previousMonth.getMonth());
	};

	const invoiceableDates = [];
	for (let i = 0; i < 12; i++) {
		invoiceableDates.push(sub(startOfMonth(new Date()), { months: i }));
	}
	return (
		<>
			<div className="breadcrumbs">
				<ul>
					<li>
						<Link to="/" className="link">
							Home
						</Link>
					</li>
					<li>
						<Link to="/ilos-panel" className="link">
							Billing
						</Link>
					</li>
					<li>Invoicing</li>
				</ul>
			</div>
			<div className="clear-both px-4 py-4 mt-2 bg-white rounded-lg">
				<h2>Invoicing</h2>
				<div className="divider" />
				<div className="grid grid-cols-3 gap-4">
					<div>
						<NavButton
							back
							title="Previous Month"
							onClick={handlePreviousMonth}
						/>
					</div>
					<div className="text-center">
						<select
							className="select select-sm select-bordered"
							onChange={({ target }) => setMonth(target.value)}
							value={month}
						>
							<option value="1">January</option>
							<option value="2">February</option>
							<option value="3">March</option>
							<option value="4">April</option>
							<option value="5">May</option>
							<option value="6">June</option>
							<option value="7">July</option>
							<option value="8">August</option>
							<option value="9">September</option>
							<option value="10">October</option>
							<option value="11">November</option>
							<option value="12">December</option>
						</select>
						<select
							className="ml-2 select select-sm select-bordered"
							onChange={({ target }) => setYear(target.value)}
							value={year}
						>
							{Array.from({ length: 5 }, (_, i) => (
								<option key={i} value={CURR_YEAR - 2 + i}>
									{CURR_YEAR - 2 + i}
								</option>
							))}
						</select>
					</div>
					<div className="text-right">
						<NavButton title="Next Month" onClick={handleNextMonth} />
					</div>
				</div>
				<table className="table mt-4 table-xs">
					<thead>
						<tr className="[&>th]:max-w-[7rem] [&>th]:truncate [&>th]:align-bottom">
							<th>Request ID</th>
							<th>
								Scheduled
								<br />
								Date
							</th>
							<th>Service</th>
							<th>Last, First</th>
							<th>DOB</th>
							<th>SKU</th>
							<th>Frequency</th>
							<th>
								Max Reimbursement
								<br />
								Amount
							</th>
							<th>Actual Cost</th>
							<th>
								Fulfilled
								<br />
								Date
							</th>
							<th>
								Fulfillment
								<br />
								Status
							</th>
							<th>Fulfill?</th>
						</tr>
					</thead>
					<tbody>
						{lineItemsQuery.isFetching && (
							<tr>
								<td colSpan={12}>
									<span className="loading loading-spinner loading-xs"></span>
								</td>
							</tr>
						)}
						{lineItemsQuery?.data?.length === 0 && (
							<tr>
								<td colSpan={12}>No referrals found</td>
							</tr>
						)}
						{lineItemsQuery?.data?.map((item) => (
							<InvoicingRow
								key={`${item.id}`}
								item={item}
								lineItemMutation={lineItemMutation}
							/>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
};

export default Invoicing;
